/**
 * Рекурсивный рендер узла дерева сущностей — docs/phase-1/05-ui.md §3.2.
 *
 * Дерево строит `buildTree` из contracts, здесь только соответствие «тип → элемент». Ни
 * одного `dangerouslySetInnerHTML`: в фазе 2 текст придёт от модели, обработавшей
 * недоверенный HTML из открытого интернета, и строка HTML в DOM была бы прямой дырой.
 * Плюс к безопасности это ещё и единственный способ сохранить структурное соответствие:
 * дерево React-узлов повторяет дерево сущностей один в один, а конкатенация тегов — нет
 * (проверено на @telegraf/entity, который на пересечении сущностей дублирует текст).
 *
 * Переводы строк НЕ превращаются в <br>: контейнер сообщения несёт `white-space: pre-wrap`
 * (§3.2). Это не косметика — при обратном чтении текста из DOM <br> сдвинул бы offset'ы.
 *
 * СМЕЩЕНИЕ ПЕРЕДАЁТСЯ ЯВНО (`offset`), а не считается заново на каждом уровне: пометки
 * сравнения версий приходят диапазонами в тексте ПОСТА, а вложенный вызов знает только свои
 * узлы. Без явного смещения пометка внутри жирного или цитаты уехала бы к началу текста.
 */

import { isAutoDetectedEntityType, type PreviewEntity, type PreviewNode } from '@smm/contracts';
import { Fragment, type JSX, type ReactNode, useState } from 'react';
import styles from './MessagePreview.module.css';
import { type DiffMarks, markText } from './marks.tsx';

/** Типы, которых в наших entities быть не может; рендер их не украшает — их ловит валидация. */
const NOT_RENDERED: ReadonlySet<string> = new Set(['custom_emoji', 'text_mention']);

/**
 * Ключи считаются от позиции в тексте, а не от индекса в массиве: смена режима превью
 * («как выйдет в канал» ↔ «только наша разметка») меняет длину массива узлов, и индексные
 * ключи переиспользовали бы состояние раскрытого спойлера на соседней сущности.
 */
export function PreviewNodes({
  nodes,
  offset = 0,
  marks = null,
}: {
  nodes: readonly PreviewNode[];
  /** Абсолютное смещение первого узла в тексте поста. */
  offset?: number;
  /** Не `null` — режим сравнения версий: текстовые листья режутся по диапазонам. */
  marks?: DiffMarks | null;
}): JSX.Element {
  const out: ReactNode[] = [];
  let cursor = offset;
  for (const node of nodes) {
    if (node.kind === 'text') {
      // Без пометок лист остаётся голой строкой: обёртка вокруг каждого листа меняла бы
      // разметку превью в обычном режиме и ничего при этом не давала.
      out.push(
        marks === null ? (
          node.text
        ) : (
          <Fragment key={`t${String(cursor)}`}>{markText(node.text, cursor, marks)}</Fragment>
        ),
      );
      cursor += node.text.length;
    } else {
      out.push(
        <EntityNode
          key={`${cursor}:${node.entity.type}`}
          entity={node.entity}
          nodes={node.children}
          offset={cursor}
          marks={marks}
        />,
      );
      cursor += node.entity.length;
    }
  }
  return <>{out}</>;
}

function EntityNode({
  entity,
  nodes,
  offset,
  marks,
}: {
  entity: PreviewEntity;
  nodes: readonly PreviewNode[];
  offset: number;
  marks: DiffMarks | null;
}): JSX.Element {
  // Оба состояния объявлены безусловно: порядок хуков не имеет права зависеть от типа
  // сущности, иначе смена режима превью перемешает состояние спойлеров и цитат.
  const [revealed, setRevealed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Дети сущности начинаются ровно там, где начинается она сама.
  const children = <PreviewNodes nodes={nodes} offset={offset} marks={marks} />;

  switch (entity.type) {
    case 'bold':
      return <b>{children}</b>;
    case 'italic':
      return <i>{children}</i>;
    case 'underline':
      return <u>{children}</u>;
    case 'strikethrough':
      return <s>{children}</s>;

    case 'spoiler':
      return (
        <button
          type="button"
          className={revealed ? styles['spoilerOpen'] : styles['spoiler']}
          aria-expanded={revealed}
          aria-label={revealed ? 'Скрытый текст раскрыт' : 'Показать скрытый текст'}
          onClick={() => {
            setRevealed(true);
          }}
        >
          {children}
        </button>
      );

    case 'code':
      return <code className={styles['code']}>{children}</code>;

    case 'pre':
      // Подпись языка рисуется через CSS `content: attr(data-lang)`, а не элементом:
      // элемент попал бы в textContent контейнера, и текст превью перестал бы совпадать
      // с текстом поста. Совпадение здесь — не педантизм, а то, что мы обещаем наружу.
      return (
        <pre className={styles['pre']} data-lang={entity.language}>
          <code>{children}</code>
        </pre>
      );

    case 'blockquote':
      return <blockquote className="tg-q">{children}</blockquote>;

    case 'expandable_blockquote':
      // Свёрнута по умолчанию — в канале так и будет. Развёрнутая цитата в превью и
      // свёрнутая в канале это разный пост для того, кто оценивает длину и ритм.
      return (
        <blockquote className="tg-q">
          <span className={expanded ? styles['quoteFull'] : styles['quoteCollapsed']}>
            {children}
          </span>
          <button
            type="button"
            className={styles['quoteToggle']}
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((v) => !v);
            }}
          >
            {expanded ? 'Свернуть' : 'Развернуть'}
          </button>
        </blockquote>
      );

    case 'text_link':
      // АДРЕС В `title`, А НЕ ПОДПИСЬЮ ПОД АНКОРОМ (правка 3.4 от 08.09.2026). Раньше под
      // каждым анкором печаталась служебная строка «↳ анкор ведёт на …». Telegram такой
      // подписи не рисует, и в посте с несколькими ссылками она удваивала объём текста в
      // пузыре — то есть ломала ровно то, ради чего панель превью существует: показать пост
      // так, как его увидит подписчик. Адрес при этом НЕ потерян: он и в `href`, и в
      // `title`, то есть виден наведением и доступен скринридеру. Полный разбор «куда ведут
      // ссылки этого поста» даёт колонка исходника и плашка карточки, а не скин сообщения.
      return (
        <a
          className="tg-a"
          href={entity.url}
          title={entity.url ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );

    default:
      if (isAutoDetectedEntityType(entity.type)) {
        // Синие, но НЕ кликабельные: эту подсветку ставит сервер, у нас её нет в entities и
        // отправить её нельзя. Пунктир и подсказка отличают её от нашей разметки.
        return (
          <span className="tg-auto" title="эту подсветку добавит сам Telegram">
            {children}
          </span>
        );
      }
      if (NOT_RENDERED.has(entity.type)) {
        // Не украшаем, но и текст не теряем: пропажа текста в превью хуже, чем отсутствие
        // оформления. Сам факт такой сущности — блокирующая ошибка уровня 1.
        return children;
      }
      return children;
  }
}
